import { useRef, useState, type ChangeEvent } from 'react'
import { PhotoType } from '@numeo/types-core'
import { Upload } from 'lucide-react'
import { Button } from '@/components/ds2'
import { useGeneralAgentChatStore } from '../../hooks/generalAgentChatStore'
import {
    CHAT_UPLOAD_ACCEPT,
    DOCUMENT_TYPE_LABEL,
    revisionResultText,
    uploadErrorText,
    uploadToLoad,
    type ChatDocumentType,
    type OpenedRevision,
} from './uploadFlow'

interface UploadButtonProps {
    loadNumber: string
    loadId?: string
    type: ChatDocumentType
    /** The button's words when the row says more than the type (e.g. "Upload a clearer POD for #4521"). */
    label?: string
    /** A rate con that opened the load's revision is handed here for the host to show for review; the host tells the chat
     *  once the user chooses, as the composer's RevisionReview does. Without it, the chat is told the revision waits on Ongoing Loads. */
    onRevision?: (revision: OpenedRevision) => void
    /** The file was filed and the chat told, with nothing left for the user to choose. Not called for a revision handed to
     *  `onRevision`: the host decides when that upload is done. */
    onUploaded?: () => void
    /** The host files the picked file itself and shows what came of it (the review drawer): nothing is said to the chat, no turn
     *  starts, and `onRevision`/`onUploaded` are not called. A throw is shown under the button as an upload error. */
    upload?: (file: File) => Promise<void>
}

/** "Upload POD for #4521" on an issue row (D15): the load and the type are fixed before the file is picked. */
export const UploadButton = ({ loadNumber, loadId, type, label: shown, onRevision, onUploaded, upload }: UploadButtonProps) => {
    // The chat message naming what was uploaded, so the agent reads the load again (FR-D2), once nothing waits on the user.
    const notify = useGeneralAgentChatStore((s) => s.notify)
    const input = useRef<HTMLInputElement>(null)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const label = shown ?? `Upload ${DOCUMENT_TYPE_LABEL[type]} for #${loadNumber}`

    const onPicked = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return
        setBusy(true)
        setError(null)
        try {
            if (upload) {
                await upload(file)
                return
            }
            const uploaded = await uploadToLoad({ loadNumber, loadId, type, file })
            if (typeof uploaded !== 'string' && onRevision) {
                onRevision(uploaded)
                return
            }
            notify(typeof uploaded === 'string' ? uploaded : revisionResultText(uploaded, { kind: 'left' }))
            onUploaded?.()
        } catch (err) {
            setError(uploadErrorText(err))
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="flex flex-col gap-1">
            <Button size="sm" variant="secondary" loading={busy} prefix={<Upload className="h-3.5 w-3.5" />} onClick={() => input.current?.click()}>
                {label}
            </Button>
            <input ref={input} type="file" accept={CHAT_UPLOAD_ACCEPT} className="hidden" aria-label={label} onChange={(e) => void onPicked(e)} />
            {busy && type === PhotoType.RATECON && (
                <span role="status" className="text-xs text-foreground-muted">
                    Reading the rate con. This can take up to two minutes.
                </span>
            )}
            {error && (
                <p role="alert" className="text-xs text-danger-soft-foreground">
                    {error}
                </p>
            )}
        </div>
    )
}
